import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { ClubDocument } from '@jupi/shared'
import { documentDownloadUrl, documentIcon, formatBytes, formatDate } from '@jupi/ui'

interface DocumentRowProps {
  document: ClubDocument
  // The category's name in the member's language.
  categoryName: string
  language: string
  // Picking a tag on a row filters by it.
  onTag: (tag: string) => void
}

// One document: the whole top of the card is the download link, and the tags
// below it are separate buttons (a button inside a link is not valid HTML).
export function DocumentRow({ document: d, categoryName, language, onTag }: DocumentRowProps) {
  const { t } = useTranslation()
  const facts = [
    categoryName,
    d.documentDate ? formatDate(`${d.documentDate}T12:00:00Z`) : null,
    formatBytes(d.sizeBytes, language),
  ].filter(Boolean)

  return (
    <Paper variant="outlined" sx={{ mb: 1 }}>
      <ButtonBase
        component="a"
        href={documentDownloadUrl(d.documentId)}
        aria-label={t('documents.download', { title: d.title })}
        sx={{ display: 'flex', width: '100%', alignItems: 'flex-start', gap: 1.5, p: 1.5, textAlign: 'left' }}
      >
        <Icon icon={documentIcon(d.contentType)} width={32} height={32} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {d.title}
          </Typography>
          {d.visibility === 'board' ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<Icon icon="mdi:lock-outline" width={16} />}
              label={t('documents.boardOnly')}
              sx={{ mt: 0.5, mr: 1 }}
            />
          ) : null}
          {d.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {d.description}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {facts.join(' · ')}
          </Typography>
        </Box>
        <Icon icon="mdi:download" width={20} height={20} />
      </ButtonBase>
      {d.tags.length > 0 ? (
        <Stack direction="row" useFlexGap spacing={0.5} sx={{ flexWrap: 'wrap', px: 1.5, pb: 1.5, pl: 7 }}>
          {d.tags.map((tag) => (
            <Chip key={tag} size="small" variant="outlined" label={tag} onClick={() => onTag(tag)} />
          ))}
        </Stack>
      ) : null}
    </Paper>
  )
}
